import {Combobox as ComboboxPrimitive} from '@base-ui/react/combobox';
import {CheckIcon, ChevronDownIcon} from 'lucide-react';

import {useDirection} from './direction';
import {Spinner} from './spinner';
import {cn} from '../../lib/utils';

import type {ComponentProps, FocusEventHandler, KeyboardEventHandler, ReactNode, Ref} from 'react';

function Combobox<Value = string>(props: ComboboxPrimitive.Root.Props<Value>) {
  return <ComboboxPrimitive.Root data-slot="combobox" {...props} />;
}

function ComboboxInput({className, ...props}: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-input"
      className={cn(
        'input-base field-focus field-error field-disabled field-placeholder',
        'flex w-full bg-background-surface py-2.5 pe-10 ps-3 leading-5',
        'text-base md:text-sm',
        className
      )}
      {...props}
    />
  );
}

function ComboboxTrigger({className, ...props}: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn(
        'pointer-events-none absolute inset-e-0 top-0 flex h-full items-center pe-3 text-fg-tertiary',
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4 shrink-0" aria-hidden />
    </ComboboxPrimitive.Trigger>
  );
}

function ComboboxPopup({className, ...props}: ComboboxPrimitive.Popup.Props) {
  return (
    <ComboboxPrimitive.Popup
      data-slot="combobox-popup"
      className={cn(
        'relative isolate z-50 w-full max-h-(--available-height) origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md bg-background-surface text-fg shadow-md ring-1 ring-fg/10 duration-100',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
        className
      )}
      {...props}
    />
  );
}

function ComboboxPositioner({
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  ...props
}: ComboboxPrimitive.Positioner.Props) {
  const direction = useDirection();
  return (
    <ComboboxPrimitive.Positioner
      data-slot="combobox-positioner"
      side={side}
      sideOffset={sideOffset}
      align={align}
      className="isolate z-50 w-(--anchor-width)"
      dir={direction}
      {...props}
    />
  );
}

function ComboboxList({className, ...props}: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List data-slot="combobox-list" className={cn('p-1', className)} {...props} />
  );
}

function ComboboxItem({className, children, ...props}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        'group relative flex w-full cursor-default select-none items-center gap-2 rounded-sm ps-3 pe-8 py-2 text-sm text-fg outline-hidden',
        'hover:bg-background-surface_hover data-highlighted:bg-background-surface_hover',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute end-2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="pointer-events-none size-3.5" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxEmpty({className, ...props}: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      // Base UI always renders this element childless when there are items — empty:hidden collapses it.
      className={cn('py-2 text-center text-sm text-fg-tertiary empty:hidden', className)}
      {...props}
    />
  );
}

function ComboboxGroup({className, ...props}: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn('py-1 first:pt-0', className)}
      {...props}
    />
  );
}

function ComboboxGroupLabel({className, ...props}: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-group-label"
      className={cn(
        'flex items-center gap-1.5 px-3 pb-1 pt-2 text-xs font-medium text-fg-tertiary',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className
      )}
      {...props}
    />
  );
}

function ComboboxPortal(props: ComponentProps<typeof ComboboxPrimitive.Portal>) {
  return <ComboboxPrimitive.Portal data-slot="combobox-portal" {...props} />;
}

/** The anchor row every combobox field renders: adornment, input, loading-spinner/chevron swap. */
function ComboboxInputShell({
  id,
  hasError,
  placeholder,
  disabled,
  readOnly,
  startAdornment,
  loading,
  className,
  children,
  onBlur,
  onFocus,
  onKeyDown,
  inputRef
}: {
  id: string;
  hasError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  startAdornment?: ReactNode;
  loading?: boolean;
  className?: string;
  children?: ReactNode;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  inputRef?: Ref<HTMLInputElement>;
}) {
  return (
    <div className="relative">
      {startAdornment && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-s-3 top-1/2 -translate-y-1/2 text-fg-tertiary"
        >
          {startAdornment}
        </span>
      )}
      <ComboboxInput
        id={id}
        ref={inputRef}
        aria-invalid={hasError || undefined}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        className={cn(startAdornment && 'ps-9', className)}
      />
      {loading ? (
        <span
          data-slot="combobox-loader"
          className="pointer-events-none absolute inset-e-0 top-0 flex h-full items-center pe-3 text-fg-tertiary"
          aria-hidden
        >
          <Spinner />
        </span>
      ) : (
        <ComboboxTrigger />
      )}
      {children}
    </div>
  );
}

/** Forwards only genuine keystrokes (`reason === 'input-change'`) — Base UI also fires `onInputValueChange` for its own selection bookkeeping. */
function onComboboxUserInputChange(
  handler: (text: string) => void
): NonNullable<ComboboxPrimitive.Root.Props<unknown>['onInputValueChange']> {
  return (text, eventDetails) => {
    if (eventDetails.reason !== 'input-change') {
      eventDetails.cancel();
      return;
    }
    handler(text);
  };
}

export {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxInputShell,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxPortal,
  ComboboxPositioner,
  ComboboxTrigger,
  onComboboxUserInputChange
};
